import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { PawPrint, Loader2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import api, { setToken } from "@/lib/api";

/**
 * /demo — public, password-less entry into a sandboxed demo workspace.
 * Calls POST /api/auth/demo-login, swaps the user's token to a demo JWT,
 * then full-reloads /app so AuthProvider re-bootstraps with the new token.
 */
export default function DemoLandingPage() {
  const [error, setError] = useState(null);
  const triggered = useRef(false);

  useEffect(() => {
    if (triggered.current) return;
    triggered.current = true;

    async function launch() {
      try {
        const res = await api.post("/auth/demo-login");
        setToken(res.data.access_token);
        // Hard navigation forces AuthProvider to re-bootstrap and pick up
        // the new demo token via /auth/me — avoids any state-sync flakiness.
        window.setTimeout(() => {
          window.location.replace("/app");
        }, 600);
      } catch (e) {
        const detail = e?.response?.data?.detail || e.message;
        setError(typeof detail === "string" ? detail : "Could not start the demo.");
      }
    }
    launch();
  }, []);

  return (
    <div
      data-testid="demo-launching-page"
      className="flex min-h-screen items-center justify-center bg-gradient-to-br from-[#fdf8f3] via-amber-50 to-[#fdf8f3] p-4"
    >
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-md rounded-3xl border border-amber-200 bg-white p-8 shadow-2xl shadow-amber-100/60"
      >
        <div className="mb-5 flex items-center gap-3">
          <div className="rounded-2xl bg-slate-900 p-3 text-amber-200 shadow-sm">
            <PawPrint size={24} />
          </div>
          <div>
            <h1 className="text-xl font-bold tracking-tight">Pawbook Demo</h1>
            <p className="text-sm text-slate-600">No signup required</p>
          </div>
        </div>

        {error ? (
          <div data-testid="demo-error" className="space-y-4">
            <div className="flex items-start gap-2 rounded-xl bg-red-50 p-3 text-sm text-red-800">
              <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
            <Link to="/" data-testid="demo-error-home">
              <Button variant="outline" className="w-full rounded-2xl">
                Back to home
              </Button>
            </Link>
          </div>
        ) : (
          <div data-testid="demo-launching-state" className="space-y-3">
            <div className="flex items-center gap-2 text-sm text-slate-700">
              <Loader2 size={16} className="animate-spin text-amber-600" />
              Spinning up a clean sandbox with 8 sample dogs…
            </div>
            <ul className="space-y-1.5 text-xs text-slate-500">
              <li>• Sample leads across all 6 pipeline stages</li>
              <li>• Real sessions, payments, and stage history</li>
              <li>• Conversion analytics ready to explore</li>
            </ul>
            <p className="pt-2 text-[11px] uppercase tracking-wider text-amber-700">
              Demo data resets on each visit — nothing you do here touches production
            </p>
          </div>
        )}
      </motion.div>
    </div>
  );
}
